<template>
  <component
    :is="cellElement"
    class="sf-table-header-cell"
    :class="cellClass"
    :type="sortable ? 'button' : undefined"
    :data-testid="`sf-header-${headerIndex}`"
  >
    <span
      :class="cellTextClass"
      :style="cellLinkStyle"
      data-testid="sf-header-cell-text"
    >
      <slot />
    </span>
    <i
      v-if="sortable"
      data-testid="sf-header-cell-arrow"
      :class="[styles.arrow, sortingArrow]"
    />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { TableSortDirections, TableVariants } from './constants'
import { tableSortDirections, tableVariants } from './constants'
import { useTableStyles } from './use-cell-styles'

interface Props {
  align?: 'left' | 'right'
  headerId: string
  headerIndex: number
  maxHeaderCellWidth?: number | null
  sortable: boolean
  sortBy?: string
  sortDirection?: TableSortDirections
  variant?: TableVariants
}

const props = withDefaults(defineProps<Props>(), {
  align: 'left',
  maxHeaderCellWidth: undefined,
  sortable: true,
  sortBy: '',
  sortDirection: tableSortDirections.NONE,
  variant: tableVariants.DEFAULT,
})

const { styles } = useTableStyles(() => props.variant)

const cellElement = computed(() => {
  // A th takes no focus of its own, so the sort has to hang off an element the
  // keyboard can reach. The click still bubbles to the th that handles it.
  return props.sortable ? 'button' : 'span'
})

const cellClass = computed(() => {
  return {
    'sf-a': props.sortable,
    'sf-table-header-cell--button': props.sortable,
    'sf-table-header-cell--align-right': props.align === 'right',
  }
})

const cellTextClass = computed(() => {
  return {
    'sf-inline-block': !!props.maxHeaderCellWidth,
    'sf-truncate': !!props.maxHeaderCellWidth,
    'sf-noscroll': !!props.maxHeaderCellWidth,
  }
})

const cellLinkStyle = computed(() => {
  return props.maxHeaderCellWidth
    ? {
        maxWidth: `${props.maxHeaderCellWidth - 20}px`,
      }
    : {}
})

const sortingArrow = computed(() => {
  const { headerId, sortBy, sortDirection, maxHeaderCellWidth } = props

  return {
    'fa fa-sort-up':
      headerId === sortBy && sortDirection === tableSortDirections.ASC,
    'fa fa-sort-down':
      headerId === sortBy && sortDirection === tableSortDirections.DESC,
    'fa fa-sort': headerId !== sortBy,
    'sf-inline-block': !!maxHeaderCellWidth,
  }
})
</script>

<style lang="scss">
.sf-table-header-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;

  &--align-right {
    justify-content: right;
  }

  &--button {
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    color: inherit;
    text-align: inherit;
    cursor: pointer;
  }
}
</style>
